import { IGroupConfig } from './igroups.config';

// Permission group configuration for the Socrates CPR (Constructive Planning for Results) platform.
//
// CPR is a structured collective reasoning system. Groups govern access to the idea lifecycle,
// floor management, community moderation, and platform operations — not content creation
// in the traditional sense. The state machine (Draft → Rising → On the Floor → Pillar) is
// enforced at the application layer; these groups gate which users can perform which actions
// at each stage.
//
// The [ENV] token is replaced at runtime with process.env.APP_ENV, which namespaces
// Cognito user pool groups per deployment (e.g. "dev", "staging", "prod").
// This is what makes the permission system multi-tenant-safe: each environment gets
// its own isolated set of Cognito groups, and each tenant org's users are assigned
// to the appropriate [ENV]_* groups in their shared user pool.
//
// Access level semantics (higher always supersedes lower):
//   READ   (1) — view only
//   UPDATE (2) — create and modify
//   ADMIN  (3) — full control including state transitions and privileged actions
//
// SUPER_ADM bypasses all permission checks — reserved for platform operators.
//
// NOTE: CPR's fine-grained onboarding state (REGISTERED_ORIENTATION_ONLY →
// PILLAR_REVIEW_REQUIRED → FLOOR_PARTICIPATION_REQUIRED → ACTIVE_MEMBER) and
// trust tiers (T0–T3) are enforced by application logic, not by these Cognito groups.
// These groups represent the coarse-grained operator and org-admin roles that sit
// above the user state machine.
//
// Organization provisioning is intentionally absent — each org gets its own Cognito
// user pool and is stood up by a separate ops tool, not by this application.

export class SocratesGroupConfig extends IGroupConfig {

	private _groups = {
		permissions: [
			// ─── Platform super-admin ────────────────────────────────────────────────
			// Bypasses all permission checks. Reserved for platform operators only.
			// Does not appear as a menu item.
			{
				permission: 'ADMIN',
				accessLevels: [
					{ groupName: '[ENV]_SUPER_ADM', label: 'ADMIN', accessLevel: 10000000 },
				],
			},

			// ─── IdeaManagement ───────────────────────────────────────────────────────
			// Governs the full idea lifecycle and floor operations:
			//   Draft, Rising, On the Floor, Amendment Cycle, Approved Pillar, Archived.
			// Consolidates the former IdeaManagement, FloorManagement, and PillarManagement
			// domains — org admins own all three concerns together.
			//
			// READ   — view ideas, Rising queue, floor slots, Pillars, and amendment packets
			// UPDATE — baseline for ACTIVE_MEMBERs: submit drafts, create contributions
			//          (subject to trust-tier throttles enforced by application logic)
			// ADMIN  — promote Rising → Floor, open/close floor windows, create amendment
			//          packets, trigger consensus snapshots, pause/rollback idea state,
			//          archive ideas, seed or archive Pillars
			{
				permission: 'IdeaManagement',
				accessLevels: [
					{ groupName: '[ENV]_IDEA_RD',  label: 'READ',   accessLevel: 1 },
					{ groupName: '[ENV]_IDEA_UPD', label: 'UPDATE', accessLevel: 2 },
					{ groupName: '[ENV]_IDEA_ADM', label: 'ADMIN',  accessLevel: 3 },
				],
			},

			// ─── UserManagement ───────────────────────────────────────────────────────
			// Invite, update, deactivate, and assign Cognito group roles to users within
			// a tenant org. Does NOT control onboarding state transitions (those are
			// application-enforced via account_state on organization_users).
			{
				permission: 'UserManagement',
				accessLevels: [
					{ groupName: '[ENV]_USR_MGT_RD',  label: 'READ',   accessLevel: 1 },
					{ groupName: '[ENV]_USR_MGT_UPD', label: 'UPDATE', accessLevel: 2 },
					{ groupName: '[ENV]_USR_MGT_ADM', label: 'ADMIN',  accessLevel: 3 },
				],
			},

			// ─── ModerationManagement ─────────────────────────────────────────────────
			// Governs internal safety and anti-gaming operations: applying cooling-off
			// states, disabling accounts, rolling back idea state, pausing ideas, and
			// reviewing moderation action logs. All moderation is invisible to other users
			// (no public moderation theater). READ gives read-only access to the internal
			// moderation log. ADMIN allows all moderation actions.
			{
				permission: 'ModerationManagement',
				accessLevels: [
					{ groupName: '[ENV]_MOD_RD',  label: 'READ',  accessLevel: 1 },
					{ groupName: '[ENV]_MOD_ADM', label: 'ADMIN', accessLevel: 3 },
				],
			},

			// ─── ReportingManagement ──────────────────────────────────────────────────
			// View platform health metrics and audit trails: idea signal snapshots,
			// consensus snapshot history, amendment cycle counts, and onboarding
			// completion rates. These are internal operational views — they must never
			// surface identity-correlated data or public engagement rankings.
			// ADMIN adds data export capabilities.
			{
				permission: 'ReportingManagement',
				accessLevels: [
					{ groupName: '[ENV]_REPORT_RD',  label: 'READ',  accessLevel: 1 },
					{ groupName: '[ENV]_REPORT_ADM', label: 'ADMIN', accessLevel: 3 },
				],
			},

			// ─── ConfigManagement ─────────────────────────────────────────────────────
			// Org-level platform configuration: floor window duration defaults, vote
			// thresholds (default 70%, org-tunable), objection budget limits, onboarding
			// required pillar set, trust-tier advancement parameters, and org-level
			// notification defaults. Kept separate from IdeaManagement so org admins
			// can tune system behavior without touching the idea lifecycle directly.
			{
				permission: 'ConfigManagement',
				accessLevels: [
					{ groupName: '[ENV]_CONFIG_RD',  label: 'READ',   accessLevel: 1 },
					{ groupName: '[ENV]_CONFIG_UPD', label: 'UPDATE', accessLevel: 2 },
					{ groupName: '[ENV]_CONFIG_ADM', label: 'ADMIN',  accessLevel: 3 },
				],
			},
		],
	};

	constructor() {
		super();
	}

	get permissions(): any[] {
		return this._groups.permissions;
	}
}
